import { useRef, useLayoutEffect, useCallback } from 'react';

export interface OnResizeFunctionParameters {
  width?: number;
  height?: number;
}

export type OnResizeFunction = (value: OnResizeFunctionParameters) => void;

const useResizeObserver = (onResize: OnResizeFunction, isResizeListenerEnabled = true) => {
  const elementRef = useRef<HTMLElement | null>(null);

  const handleResize = useCallback(() => {
    if (elementRef.current) {
      const { height, width } = elementRef.current.getBoundingClientRect();
      onResize({ height, width });
    }
  }, [onResize]);

  useLayoutEffect(() => {
    const currentElement = elementRef.current;

    if (currentElement) {
      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          if (entry.target === currentElement) {
            handleResize();
          }
        }
      });

      resizeObserver.observe(currentElement);

      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [handleResize]);

  const setRef = useCallback(
    (el: HTMLElement | null) => {
      if (isResizeListenerEnabled) {
        elementRef.current = el;
        if (el) {
          handleResize(); // Trigger a resize when the ref is set
        }
      }
    },
    [handleResize, isResizeListenerEnabled]
  );

  return [setRef] as const;
};

export default useResizeObserver;
